import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { MAX_ZOOM, MIN_ZOOM } from '../components/views/FebView/constants';
import { ThreeRefs } from '../components/views/FebView/types';
import { EnvironmentModel } from '../components/views/StoreBuilderDetailsView/environments';
import {
  ISceneManager,
  LightingConfiguration,
  SceneConfiguration,
  TextureOptions,
} from './interfaces';

export class SceneManager implements ISceneManager {
  private scene: THREE.Scene | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private camera: THREE.Camera | null = null;
  private controls: OrbitControls | null = null;
  private textureLoader: THREE.TextureLoader;
  private animationId: number | null = null;
  private resizeHandler: (() => void) | null = null;

  constructor() {
    this.textureLoader = new THREE.TextureLoader();
  }

  /**
   * Initialize the Three.js scene with renderer, camera, and controls
   */
  public initializeScene(
    mountElement: HTMLDivElement,
    refs: ThreeRefs,
    environment: EnvironmentModel,
    config: SceneConfiguration = {}
  ): () => void {
    const {
      background = new THREE.Color(0xf0f0f0),
      frustumSize = 15,
      shadowsEnabled = true,
      antialias = true,
      pixelRatio = Math.min(window.devicePixelRatio, 2),
    } = config;

    // Scene setup
    this.scene = new THREE.Scene();
    refs.scene.current = this.scene;
    this.scene.background =
      typeof background === 'number' ? new THREE.Color(background) : background;

    // Camera setup
    const containerAspect =
      mountElement.clientWidth / mountElement.clientHeight;
    this.camera = new THREE.OrthographicCamera(
      (frustumSize * containerAspect) / -2,
      (frustumSize * containerAspect) / 2,
      frustumSize / 2,
      frustumSize / -2,
      0.1,
      1000
    );
    refs.camera.current = this.camera;

    this.camera.position.set(0, 10, 0);
    this.camera.lookAt(0, 0, 0);
    const orthoCamera = this.camera as THREE.OrthographicCamera;
    orthoCamera.zoom = 2;
    orthoCamera.updateProjectionMatrix();

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({
      antialias,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
      precision: 'highp',
      alpha: true,
    });
    refs.renderer.current = this.renderer;

    this.renderer.setSize(mountElement.clientWidth, mountElement.clientHeight);
    this.renderer.setPixelRatio(pixelRatio);

    if (shadowsEnabled) {
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    mountElement.appendChild(this.renderer.domElement);

    // Controls setup
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    refs.controls.current = this.controls;
    this.setupControls();

    // Setup floor plane
    this.setupFloorPlane(refs, environment);

    // Setup lighting
    this.setupDefaultLighting();

    // Setup resize handler
    this.setupResizeHandler(mountElement, refs, frustumSize);

    // Start animation loop
    this.startAnimationLoop(refs);

    // Return cleanup function
    return this.getCleanupFunction(mountElement, refs);
  }

  /**
   * Setup default lighting for the scene
   */
  public setupDefaultLighting(config: LightingConfiguration = {}): void {
    if (!this.scene) return;

    const {
      ambientIntensity = 5,
      ambientColor = 0xffffff,
      directionalIntensity = 1,
      directionalColor = 0xffffff,
    } = config;

    // Add directional light
    const directionalLight = new THREE.DirectionalLight(
      directionalColor,
      directionalIntensity
    );
    directionalLight.position.set(0, 10, 0);
    this.scene.add(directionalLight);

    // Add ambient light
    const ambientLight = new THREE.AmbientLight(ambientColor, ambientIntensity);
    this.scene.add(ambientLight);
  }

  /**
   * Add point light at specific position
   */
  public addPointLight(
    position: THREE.Vector3,
    config: LightingConfiguration = {}
  ): THREE.PointLight | null {
    if (!this.scene) return null;

    const {
      pointLightIntensity = 1,
      pointLightColor = 0xffffff,
      pointLightDistance = 100,
    } = config;

    const pointLight = new THREE.PointLight(
      pointLightColor,
      pointLightIntensity,
      pointLightDistance
    );
    pointLight.position.copy(position);
    this.scene.add(pointLight);

    return pointLight;
  }

  /**
   * Load and apply texture to floor plane
   */
  public updateFloorTexture(
    floorPlane: THREE.Mesh,
    textureSource: string,
    options: TextureOptions = {}
  ): Promise<THREE.Texture> {
    const {
      minFilter = THREE.LinearFilter,
      magFilter = THREE.LinearFilter,
      generateMipmaps = false,
      wrapS = THREE.ClampToEdgeWrapping,
      wrapT = THREE.ClampToEdgeWrapping,
    } = options;

    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        textureSource,
        (loadedTexture) => {
          loadedTexture.minFilter = minFilter;
          loadedTexture.magFilter = magFilter;
          loadedTexture.generateMipmaps = generateMipmaps;
          loadedTexture.wrapS = wrapS;
          loadedTexture.wrapT = wrapT;
          loadedTexture.needsUpdate = true;

          if (this.renderer) {
            loadedTexture.anisotropy =
              options.anisotropy !== undefined
                ? options.anisotropy
                : this.renderer.capabilities.getMaxAnisotropy();
          }

          const material = floorPlane.material as THREE.MeshBasicMaterial;
          material.map = loadedTexture;
          material.needsUpdate = true;

          resolve(loadedTexture);
        },
        undefined,
        (error) => {
          console.error('Error loading texture:', error);
          reject(error);
        }
      );
    });
  }

  /**
   * Remove all objects from scene and dispose of resources
   */
  public clearScene(): void {
    if (!this.scene) return;

    // Remove all objects from scene
    while (this.scene.children.length > 0) {
      const object = this.scene.children[0];
      this.scene.remove(object);

      // Dispose of geometries and materials
      if (object instanceof THREE.Mesh) {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => {
              this.disposeMaterial(material);
            });
          } else {
            this.disposeMaterial(object.material);
          }
        }
      }
    }
  }

  /**
   * Dispose of material and its textures
   */
  private disposeMaterial(material: THREE.Material): void {
    // Dispose of textures
    const materialWithTextures = material as any;
    Object.keys(materialWithTextures).forEach((key) => {
      if (materialWithTextures[key] && materialWithTextures[key].isTexture) {
        materialWithTextures[key].dispose();
      }
    });

    // Dispose of material
    material.dispose();
  }

  /**
   * Remove specific object from scene
   */
  public removeFromScene(object: THREE.Object3D): void {
    if (!this.scene) return;

    this.scene.remove(object);

    // Dispose of resources if it's a mesh
    if (object instanceof THREE.Mesh) {
      if (object.geometry) {
        object.geometry.dispose();
      }
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach((material) => {
            this.disposeMaterial(material);
          });
        } else {
          this.disposeMaterial(object.material);
        }
      }
    }
  }

  /**
   * Add object to scene
   */
  public addToScene(object: THREE.Object3D): void {
    if (!this.scene) return;
    this.scene.add(object);
  }

  /**
   * Get reference to the scene
   */
  public getScene(): THREE.Scene | null {
    return this.scene;
  }

  /**
   * Get reference to the renderer
   */
  public getRenderer(): THREE.WebGLRenderer | null {
    return this.renderer;
  }

  /**
   * Get reference to the camera
   */
  public getCamera(): THREE.Camera | null {
    return this.camera;
  }

  /**
   * Setup orbit controls with default settings
   */
  private setupControls(): void {
    if (!this.controls) return;

    this.controls.enableDamping = false;
    this.controls.enableRotate = false;
    this.controls.maxPolarAngle = Math.PI / 2;
    this.controls.minPolarAngle = 0;
    this.controls.enablePan = true;
    this.controls.enableZoom = true;
    this.controls.panSpeed = 0.8;
    this.controls.zoomSpeed = 0.8;
    this.controls.minDistance = 2;
    this.controls.maxDistance = 15;
    this.controls.minZoom = MIN_ZOOM;
    this.controls.maxZoom = MAX_ZOOM;
    this.controls.keyPanSpeed = 0.8;
    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }

  /**
   * Setup floor plane with environment texture
   */
  private setupFloorPlane(
    refs: ThreeRefs,
    environment: EnvironmentModel
  ): void {
    if (!this.scene) return;

    const envWidth = environment.width;
    const envHeight = environment.height;
    const imageAspect = envWidth / envHeight;

    const planeWidth = 15;
    const planeHeight = planeWidth / imageAspect;
    const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);

    const material = new THREE.MeshBasicMaterial({
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
      depthTest: true,
    });

    const plane = new THREE.Mesh(geometry, material);
    plane.rotation.x = -Math.PI / 2;
    plane.userData.isInteractive = false;
    plane.userData.isFloorPlane = true;
    refs.floorPlane.current = plane;
    this.scene.add(plane);

    // Load initial texture
    this.updateFloorTexture(plane, environment.renders.renderedWithGrid);
  }

  /**
   * Setup resize handler
   */
  private setupResizeHandler(
    mountElement: HTMLDivElement,
    _refs: ThreeRefs,
    frustumSize: number
  ): void {
    this.resizeHandler = () => {
      if (!mountElement || !this.camera || !this.renderer) return;

      const width = mountElement.clientWidth;
      const height = mountElement.clientHeight;
      const newAspect = width / height;
      const orthoCamera = this.camera as THREE.OrthographicCamera;

      orthoCamera.left = (frustumSize * newAspect) / -2;
      orthoCamera.right = (frustumSize * newAspect) / 2;
      orthoCamera.top = frustumSize / 2;
      orthoCamera.bottom = frustumSize / -2;

      orthoCamera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    };

    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Start animation loop
   */
  private startAnimationLoop(_refs: ThreeRefs): void {
    const animate = () => {
      this.animationId = requestAnimationFrame(animate);

      if (!this.scene || !this.camera || !this.renderer) return;

      if (this.controls) {
        this.controls.update();
      }

      this.renderer.render(this.scene, this.camera);
    };

    animate();
  }

  /**
   * Get cleanup function for disposing resources
   */
  private getCleanupFunction(
    mountElement: HTMLDivElement,
    _refs: ThreeRefs
  ): () => void {
    return () => {
      // Stop animation loop
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }

      // Remove resize handler
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }

      // Clear scene
      this.clearScene();

      // Remove renderer from DOM
      if (mountElement && this.renderer?.domElement) {
        mountElement.removeChild(this.renderer.domElement);
      }

      // Dispose renderer
      if (this.renderer) {
        this.renderer.dispose();
        this.renderer = null;
      }

      // Clear controls
      if (this.controls) {
        this.controls.dispose();
        this.controls = null;
      }

      // Clear references
      this.scene = null;
      this.camera = null;
    };
  }

  /**
   * Dispose of all resources and cleanup
   */
  public dispose(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    this.clearScene();

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer = null;
    }

    if (this.controls) {
      this.controls.dispose();
      this.controls = null;
    }

    this.scene = null;
    this.camera = null;
  }
}

// Export singleton instance
export const sceneManager = new SceneManager();
